/**
 * quick-add.js
 * ——————————————————————————————————————————————————————————————————————
 * Defines a <quick-add-modal> custom element that fetches and injects
 * a <product-info> (or .product) snippet on demand, without ever
 * blowing up if something’s missing.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Only define once
  if (customElements.get('quick-add-modal')) return;

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        // our target container inside the modal
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // if it’s missing, warn and don’t wire up any event listeners
        if (!this.modalContent) {
          console.warn('⚠️ quick-add-modal: modalContent is null – skipping setup');
          return;
        }

        // after product-info block fires its "loaded" event, we can intercept
        this.addEventListener('product-info:loaded', ({ target }) => {
          if (typeof target.addPreProcessCallback === 'function') {
            target.addPreProcessCallback(this.preprocessHTML.bind(this));
          }
        });
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then(res => res.text())
          .then(htmlString => {
            const doc = new DOMParser().parseFromString(htmlString, 'text/html');
            // primary lookup is <product-info>, fallback to .product
            const productEl =
              doc.querySelector('product-info') ||
              doc.querySelector('.product');

            if (!productEl) {
              console.warn('❌ quick-add-modal: <product-info>/.product not found');
              return;
            }

            this.preprocessHTML(productEl);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productEl.outerHTML);

            // re-init any shopify widgets inside the injected markup
            if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch(err => console.error('quick-add-modal fetch error', err))
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(el) {
        // safety guard
        if (!el || !this.modalContent) return;

        // carry over any color-/gradient- classes
        el.classList.forEach(c => {
          if (c.startsWith('color-') || c === 'gradient') {
            this.modalContent.classList.add(c);
          }
        });

        this.preventDuplicatedIDs(el);
        this.removeDOMElements(el);
        this.removeGalleryListSemantic(el);
        this.updateImageSizes(el);
        this.preventVariantURLSwitching(el);
      }

      preventVariantURLSwitching(el) {
        el.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(el) {
        el.querySelectorAll('pickup-availability, product-modal, modal-dialog')
          .forEach(node => node.remove());
      }

      preventDuplicatedIDs(el) {
        const sectionId = el.dataset.section;
        if (!sectionId) return;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        // rewrite IDs in the markup
        el.innerHTML = el.innerHTML.replaceAll(oldId, newId);

        // rewrite data attributes
        for (const attr of Array.from(el.attributes)) {
          if (attr.value.includes(oldId)) {
            el.setAttribute(attr.name, attr.value.replaceAll(oldId, newId));
          }
        }

        el.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(el) {
        const galleryList = el.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;
        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]')
          .forEach(li => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(el) {
        const product = el.querySelector('.product');
        if (!product?.classList.contains('product--columns')) return;

        const imgs = product.querySelectorAll('.product__media img');
        if (!imgs.length) return;

        let sizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          sizes = sizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          sizes = sizes.replace('715px', '495px');
        }

        imgs.forEach(img => img.setAttribute('sizes', sizes));
      }
    }
  );
});
